import { Dispatch } from 'redux';

import { MainStoreErrorAction, setError } from 'modules/main/store/ducks/error';
import {
  MainStoreMainSearchAction,
  clearResults,
} from 'modules/main/store/ducks/mainSearch';
import {
  deleteListing as apiDeleteListing,
  fetchListing as apiFetchListing,
  hideListing as apiHideListing,
  unhideListing as apiUnhideListing,
} from 'modules/orgDashboard/api/orgDashboardApiListing';
import { OrgDashboardApiListing } from 'modules/orgDashboard/api/types/OrgDashboardApiListing';

// Types

export type MainStoreOrgDashboardListingsListing = OrgDashboardApiListing & {
  isFetching: boolean;
};

export type MainStoreOrgDashboardListingsState = {
  byId: Record<string, MainStoreOrgDashboardListingsListing>;
};

type FetchListingRequestAction = {
  type: 'ORG_DASHBOARD_FETCH_LISTING_REQUEST';
  listingId: string;
};

type FetchListingSuccessAction = {
  type: 'ORG_DASHBOARD_FETCH_LISTING_SUCCESS';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listing: Record<string, any>;
};

type ResetListingAction = {
  type: 'ORG_DASHBOARD_RESET_LISTING';
};

export type MainStoreOrgDashboardListingsAction =
  | FetchListingRequestAction
  | FetchListingSuccessAction
  | ResetListingAction;

// Reducer

const initialState: MainStoreOrgDashboardListingsState = {
  byId: {},
};

export function listingsReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: MainStoreOrgDashboardListingsState = initialState,
  action: MainStoreOrgDashboardListingsAction,
) {
  switch (action.type) {
    case 'ORG_DASHBOARD_FETCH_LISTING_REQUEST':
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.listingId]: {
            ...state.byId[action.listingId],
            id: action.listingId,
            isFetching: true,
          },
        },
      };

    case 'ORG_DASHBOARD_FETCH_LISTING_SUCCESS':
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.listing.id]: { ...action.listing, isFetching: false },
        },
      };

    case 'ORG_DASHBOARD_RESET_LISTING':
      return initialState;

    default:
      return state;
  }
}

// Actions

const fetchListingRequest = (listingId: string): FetchListingRequestAction => ({
  type: 'ORG_DASHBOARD_FETCH_LISTING_REQUEST',
  listingId,
});

const fetchListingSuccess = (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listing: Record<string, any>,
): FetchListingSuccessAction => ({
  type: 'ORG_DASHBOARD_FETCH_LISTING_SUCCESS',
  listing,
});

export const fetchListing =
  ({
    orgId,
    listingType,
    listingId,
    force,
  }: {
    orgId: string;
    listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP';
    listingId: string;
    force?: boolean;
  }) =>
  (
    dispatch: Dispatch<
      MainStoreOrgDashboardListingsAction | MainStoreErrorAction
    >,
    getState: () => {
      orgDashboard: { listings: MainStoreOrgDashboardListingsState };
    },
  ) => {
    const state = getState();
    const listing = state.orgDashboard.listings.byId[listingId];

    if (!listing || force) {
      dispatch(fetchListingRequest(listingId));
      apiFetchListing(orgId, listingType, listingId)
        .then((fetchedListing) => {
          dispatch(fetchListingSuccess(fetchedListing));
        })
        .catch((error) => {
          dispatch(setError(error));
        });
    }
  };

export const updateListing = fetchListingSuccess;

export const hideListing =
  ({
    orgId,
    listingType,
    listingId,
  }: {
    orgId: string;
    listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP';
    listingId: string;
  }) =>
  (
    dispatch: Dispatch<
      MainStoreOrgDashboardListingsAction | MainStoreMainSearchAction
    >,
  ) =>
    apiHideListing({
      orgId,
      listingType,
      listingId,
    })
      .then((listing) => {
        dispatch(updateListing(listing));
        dispatch(clearResults());
      })
      .catch(setError);

export const unhideListing =
  ({
    orgId,
    listingType,
    listingId,
  }: {
    orgId: string;
    listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP';
    listingId: string;
  }) =>
  (
    dispatch: Dispatch<
      MainStoreOrgDashboardListingsAction | MainStoreMainSearchAction
    >,
  ) =>
    apiUnhideListing({
      orgId,
      listingType,
      listingId,
    })
      .then((listing) => {
        dispatch(updateListing(listing));
        dispatch(clearResults());
      })
      .catch(setError);

export const deleteListing =
  ({
    orgId,
    listingType,
    listingId,
  }: {
    orgId: string;
    listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP';
    listingId: string;
  }) =>
  (
    dispatch: Dispatch<
      MainStoreOrgDashboardListingsAction | MainStoreMainSearchAction
    >,
  ) =>
    apiDeleteListing({
      orgId,
      listingType,
      listingId,
    })
      .then((deletedListing) => {
        dispatch(updateListing(deletedListing));
        dispatch(clearResults());
      })
      .catch(setError);

export const resetListing =
  () => (dispatch: Dispatch<MainStoreOrgDashboardListingsAction>) =>
    dispatch({
      type: 'ORG_DASHBOARD_RESET_LISTING',
    });
